import React, { useState } from 'react';
import {
  Badge, Box, Button, HStack, Icon, IconButton, Input, Link, Select, Td, Text, Tooltip, Tr, useColorModeValue, useToast,
} from '@chakra-ui/react';
import { FiCheck, FiCopy, FiEye } from 'react-icons/fi';
import { previewPdf } from '../../utils/openPdf';
import {
  SalesBibleRow as Row, SalesState, STATE_MEANING, paymentLabelOf,
} from '../../hooks/useSalesBible';
import { SalesBibleFields } from '../../hooks/useSalesBibleActions';
import usePlatforms from '../../hooks/usePlatforms';
import StaffHoverLink from './StaffHoverLink'; // item 1461

// item 623 — the STATUS badge colour per state (In Progress → Pending → Completed → Declined → Voided).
export const STATE_COLOR: Record<SalesState, string> = {
  'In Progress': 'teal',
  Pending: 'blue',
  Completed: 'purple',
  Declined: 'red',
  Voided: 'gray',
};

const YES_NO_NA = ['Yes', 'No', 'N/A'];

const yesNo = (v: boolean) => (
  <Badge colorScheme={v ? 'green' : 'red'} variant="subtle">{v ? 'Yes' : 'No'}</Badge>
);

interface Props {
  row: Row;
  // eslint-disable-next-line react/no-unused-prop-types
  stickyCell: Record<string, unknown>;
  onSave: (id: string, fields: SalesBibleFields) => Promise<unknown>;
  onLifecycle?: (row: Row) => void; // item 1144 — open the full rules popup
  onAssignPm?: (row: Row) => void; // item 1172 — open the staff/PM picker (lock-proof)
  onAssignLead?: (row: Row) => void; // item 1172 — open the WO leads editor in place
}

// item 177 (v2) — one Sales Bible row: read-only derived cells + the editable manual
// tracking columns. Edits commit on blur (text/date) or change (selects); the full set
// is sent each time so a single-cell edit never wipes the others.
// item 607 — a row is either a proposal-based job or a standalone work-order job. Manual
// tracking columns are editable only for proposal rows (the SalesBible endpoint is keyed
// to a proposal); WO rows show them read-only.
const SalesBibleRowEditor: React.FC<Props> = ({
  row, stickyCell, onSave, onLifecycle, onAssignPm, onAssignLead,
}) => {
  const toast = useToast();
  const { data: platforms } = usePlatforms(); // item 302 — combobox option list
  const [platform, setPlatform] = useState(row.platform);
  const [deploymentDate, setDeploymentDate] = useState(row.deploymentDate);
  const [illustrations, setIllustrations] = useState(row.illustrationsComplete);
  // item 303 — projectLeadPayoutInput is no longer manually edited; keep the value flowing
  // through commit so it isn't wiped.
  const [payout] = useState(row.projectLeadPayoutInput);
  const [notes, setNotes] = useState(row.notes);
  const pillEdge = useColorModeValue('blackAlpha.300', 'whiteAlpha.400'); // item 958 — light-mode contrast
  // item 610 → 1024 — icon visibility is handled globally by the item-877 color-scheme rule;
  // the old invert(1) filter here DOUBLE-APPLIED with it (white icon flipped back to black —
  // Chad's "same icon black issue"). No filter, ever.
  // item 1210 — WO rows edit the same manual columns now (the endpoint accepts a WO id)
  const editable = true;

  const commit = (override: Partial<SalesBibleFields> = {}) => {
    onSave(row.id, {
      platform,
      deploymentDate,
      illustrationsComplete: illustrations,
      projectLeadPayoutInput: payout,
      notes,
      ...override,
    });
  };

  const yesNoNaSelect = (value: string, onChange: (v: string) => void) => (
    <Select
      size="xs"
      minW="80px"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">—</option>
      {YES_NO_NA.map((o) => <option key={o} value={o}>{o}</option>)}
    </Select>
  );

  // item 1458 (Dustin via Chad: "all links from sales bible should open in a new tab") —
  // every click-through on the board is a REAL anchor in a new tab (middle-click / cmd-click
  // behave too). The board itself never navigates away.
  const jobHref = row.kind === 'workOrder' ? `/WorkOrder/${row.id}` : `/proposal/${row.id}`;
  // item 1460 (Chad: "can the job name go to the hand off") — a job with a hand-off lands on
  // its project's HAND-OFFS section; anything else still opens the proposal / WO.
  const jobNameHref = row.hasHandOff && row.projectId ? `/project/${row.projectId}#psec-handOffs` : jobHref;
  const docTitle = row.kind === 'workOrder' ? 'Open this work order' : 'Open this proposal';
  const jobNameTitle = row.hasHandOff && row.projectId ? 'Open this job\'s hand-off (project › HAND-OFFS)' : docTitle;
  // item 1460 — the eye previews the hand-off PDF in a new tab (same call as the project section)
  const previewHandOff = () => {
    previewPdf(`proposal/${row.id}/handoff-pdf`).catch(() => toast({
      status: 'error', title: 'Could not open the hand-off', duration: 6000, isClosable: true,
    }));
  };
  const newTab = (href: string, label: React.ReactNode, title?: string) => (
    <Link href={href} target="_blank" rel="noopener noreferrer" title={title} _hover={{ textDecoration: 'underline' }}>
      {label}
    </Link>
  );
  // item 1459 (Chad: "the text is not selectable… i like the copy button") — the job code
  // goes to the clipboard on one click, whatever the browser does with selection.
  const copyJobCode = async () => {
    try {
      await navigator.clipboard.writeText(row.workOrderNumber);
      toast({
        status: 'success', title: `Copied ${row.workOrderNumber}`, duration: 1500, isClosable: true,
      });
    } catch {
      toast({
        status: 'error', title: 'Could not copy — select the job code and copy it', duration: 3000, isClosable: true,
      });
    }
  };

  return (
    <Tr>
      {/* item 305 — click the job → open the proposal (or WO) in its project context
          item 1458 — in a new tab */}
      <Td
        sx={stickyCell}
        fontWeight="medium"
        whiteSpace="normal"
      >
        <HStack spacing={1} align="flex-start">
          {newTab(jobNameHref, row.jobName, jobNameTitle)}
          {row.hasHandOff && (
            <IconButton
              aria-label="Preview hand-off PDF"
              title="Preview hand-off PDF"
              icon={<FiEye />}
              size="xs"
              variant="ghost"
              flexShrink={0}
              onClick={previewHandOff}
            />
          )}
        </HStack>
      </Td>
      {/* item 1587 — PROJECT: the official project the job comes from → its project page */}
      <Td whiteSpace="normal">
        {row.projectId && row.projectName !== '—'
          ? newTab(`/project/${row.projectId}`, row.projectName, 'Open this job\'s project')
          : row.projectName}
      </Td>
      <Td>
        {/* item 623 — the STATUS badge is the state itself (In Progress / Pending / Completed /
            Declined / Voided). Completed keeps its distinct purple + check.
            item 625 — hover explains what the state means. */}
        {/* item 1144 (Chad) — hover = THIS job's graded lifecycle trail (why it's in this
            state); click = the full rules popup with links. Falls back to the generic
            meaning when a row has no trail. */}
        <Tooltip
          label={(row.lifecycle?.length ?? 0) > 0 ? (
            <Box fontSize="xs">
              {row.verdict && <Text fontWeight="bold" mb={1}>{row.verdict}</Text>}
              {row.lifecycle.map((st) => (
                <Text key={st.label} color={st.done ? 'green.300' : 'red.400'} fontWeight={st.done ? 'normal' : 'semibold'}>
                  {`${st.done ? '✓' : '✗'} ${st.label}${st.detail ? ` — ${st.detail}` : ''}`}
                </Text>
              ))}
              <Text opacity={0.7} mt={1}>Click for the full report</Text>
            </Box>
          ) : STATE_MEANING[row.state]}
          hasArrow
          placement="top"
          openDelay={200}
          bg="gray.900"
          color="whiteAlpha.900"
          borderRadius="md"
          p={3}
        >
          <Badge
            colorScheme={STATE_COLOR[row.state]}
            variant="solid"
            cursor="pointer"
            onClick={() => onLifecycle?.(row)}
          >
            {row.state === 'Completed' && <Icon as={FiCheck} mr={1} mb="-1px" />}
            {row.state}
          </Badge>
        </Tooltip>
      </Td>
      {/* item 1258 — PAYMENT: collection state decoupled from the job's status. A complete
          job can still owe money ("Luxor is 100% complete, they just owe the invoice").
          item 1261 (Chad: "not numbers") — WORDED labels tracking the billing journey:
          Deposited (money in, still billing) → Progress (finally invoiced, collecting) →
          Paid in Full. The amount owed survives on hover only. */}
      <Td whiteSpace="nowrap">
        {(() => {
          const label = paymentLabelOf(row);
          if (label === '—') return <Text as="span" opacity={0.4}>—</Text>;
          const scheme: Record<string, string> = {
            'Paid in Full': 'green', Progress: 'orange', Deposited: 'teal', Open: 'gray', 'Written Off': 'red',
          };
          const badge = (
            <Badge colorScheme={scheme[label]} variant="subtle" cursor="default">
              {label.toUpperCase()}
            </Badge>
          );
          // item 1584 — a write-off (whole or partial) always says so on hover
          const fmt = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          const hover = [
            row.owedTotal > 0 ? `Owed: ${fmt(row.owedTotal)}` : '',
            row.writtenOffTotal > 0 ? `Written off: ${fmt(row.writtenOffTotal)}` : '',
          ].filter(Boolean).join(' · ');
          return hover ? (
            <Tooltip
              label={hover}
              hasArrow
              placement="top"
              openDelay={200}
            >
              {badge}
            </Tooltip>
          ) : badge;
        })()}
      </Td>
      {/* item 1458 — INTEGRATOR → the job's project page (the sidebar lights that project up) */}
      <Td>
        {row.projectId
          ? newTab(`/project/${row.projectId}`, row.integrator, 'Open this job\'s project')
          : row.integrator}
      </Td>
      {/* item 609 — the WO number = the contractual "job code" (left of Sales Person);
          blank until a work order exists.
          item 1459 — selectable text + a one-click copy button */}
      <Td fontFamily="mono" sx={{ userSelect: 'text' }}>
        {row.workOrderNumber ? (
          <HStack spacing={1}>
            {/* item 1458b (Chad) — the number itself opens the work order in a new tab */}
            {row.workOrderId
              ? newTab(`/WorkOrder/${row.workOrderId}`, <Text as="span" sx={{ userSelect: 'text' }}>{row.workOrderNumber}</Text>, 'Open this work order')
              : <Text as="span" sx={{ userSelect: 'text' }}>{row.workOrderNumber}</Text>}
            <IconButton
              aria-label="Copy job code"
              title="Copy job code"
              icon={<FiCopy />}
              size="xs"
              variant="ghost"
              onClick={copyJobCode}
            />
          </HStack>
        ) : '—'}
      </Td>
      {/* item 612 — the customer's project-manager / staff contact (left of Sales Person).
          item 1171 (Chad: "when it is empty, I have a way to keep it accurate") — an EMPTY
          cell is a task, not a dash: Assign→ jumps to the document that owns the value
          (customer staff/PM lives on the proposal or standalone WO; the lead lives on the
          WORK ORDER, item 526). No inline dropdowns, no overrides — the real editor. */}
      {/* item 1458 — the customer PM's name → their STAFF row on the customer page.
          item 1458b — the index rows carry the PM's NAME, not the staff id (Chad: "clicking on
          Richard Reisig does not take me to the CRM"), so the anchor falls back to the name;
          CustomerStaffField matches either. */}
      <Td>
        {/* item 1461 — hover shows role · phone · email off the CRM roster; click still deep-links */}
        {row.customerStaffPm !== '—' && row.customerId && (
          <StaffHoverLink
            customerId={row.customerId}
            staffId={row.customerPmId}
            name={row.customerStaffPm}
            href={`/customer/${row.customerId}#${row.customerPmId ? `staff-${row.customerPmId}` : `staffname-${encodeURIComponent(row.customerStaffPm)}`}`}
          />
        )}
        {row.customerStaffPm !== '—' && !row.customerId && row.customerStaffPm}
        {row.customerStaffPm === '—' && (
          <Button
            size="xs"
            variant="link"
            colorScheme="orange"
            fontWeight="medium"
            onClick={() => onAssignPm?.(row)}
          >
            Assign →
          </Button>
        )}
      </Td>
      <Td>{row.salesPerson}</Td>
      <Td>
        {(() => {
          if (row.projectLead === 'Not Assigned') return null;
          // item 1270 (Chad: "I think I can only add one from the sales bible") — an ASSIGNED
          // lead is still a way back into the editor: click the name to reopen the same
          // multi-lead modal (add a co-lead, change the split). Only the button vanished
          // before; the capability was always multi.
          if (row.workOrderId || row.kind === 'workOrder') {
            return (
              <Button
                size="xs"
                variant="link"
                fontWeight="medium"
                title="Edit leads — add a co-lead or change the split"
                onClick={() => onAssignLead?.(row)}
              >
                {row.projectLead}
              </Button>
            );
          }
          return <>{row.projectLead}</>;
        })()}
        {row.projectLead === 'Not Assigned' && (
          <Button
            size="xs"
            variant="link"
            colorScheme="orange"
            fontWeight="medium"
            onClick={() => {
              // leads live on the WORK ORDER — open its real editor in place; a job with
              // no WO yet gets its lead at approval, so fall back to the proposal
              if (row.workOrderId || row.kind === 'workOrder') onAssignLead?.(row);
              else window.open(`/proposal/${row.id}`, '_blank', 'noopener'); // item 1458 — new tab
            }}
          >
            Assign →
          </Button>
        )}
      </Td>
      {/* item 606 — WHO is getting commission on this job, grouped by reason.
          item 919 (Chad) — COMPACT: the full breakdown blew row heights up; show a small
          count chip and put the per-reason detail in a hover tooltip instead. */}
      <Td>
        {row.commissionEarners.length === 0 ? (
          <Text as="span" color="gray.500">—</Text>
        ) : (
          <Tooltip
            openDelay={150}
            label={(
              <Box fontSize="xs" lineHeight="tall">
                {row.commissionEarners.map((g) => (
                  <Box key={g.reason}>
                    <Text as="span" fontWeight="semibold">{`${g.reason}: `}</Text>
                    <Text as="span">{g.names.join(', ')}</Text>
                  </Box>
                ))}
              </Box>
            )}
          >
            <Text
              as="span"
              fontSize="xs"
              whiteSpace="nowrap"
              cursor="default"
              borderWidth="1px"
              borderColor={pillEdge}
              borderRadius="full"
              px={2}
              py={0.5}
            >
              {(() => {
                const n = new Set(row.commissionEarners.flatMap((g) => g.names)).size;
                return `${n} earner${n === 1 ? '' : 's'}`;
              })()}
            </Text>
          </Tooltip>
        )}
      </Td>
      <Td>
        {editable
          ? yesNoNaSelect(illustrations, (v) => { setIllustrations(v); commit({ illustrationsComplete: v }); })
          : <Text as="span" color="gray.500">{row.illustrationsComplete || '—'}</Text>}
      </Td>
      <Td>
        {/* item 302 — search existing platforms (datalist) or type a new one; the new
            value is remembered on commit (backend get-or-create). */}
        {editable ? (
          <>
            <Input
              size="xs"
              minW="140px"
              placeholder="Platform…"
              aria-label="Platform"
              list={`platforms-${row.id}`}
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              onBlur={() => commit()}
            />
            <datalist id={`platforms-${row.id}`}>
              {(platforms ?? []).map((p) => <option key={p} value={p}>{p}</option>)}
            </datalist>
          </>
        ) : (
          <Text as="span" color="gray.500">{row.platform || '—'}</Text>
        )}
      </Td>
      {/* item 1458 — a YES is a link to the document behind it: Contracted → the job,
          Deposit Billed → the first live invoice, Final Billed → the last one */}
      <Td>{row.contracted ? newTab(jobHref, yesNo(true), 'Open the contracted job') : yesNo(false)}</Td>
      <Td>
        {row.depositBilled && row.depositInvoiceId
          ? newTab(`/invoice/${row.depositInvoiceId}`, yesNo(true), 'Open the deposit invoice')
          : yesNo(row.depositBilled)}
      </Td>
      <Td>
        {row.finalBilled && row.finalInvoiceId
          ? newTab(`/invoice/${row.finalInvoiceId}`, yesNo(true), 'Open the final invoice')
          : yesNo(row.finalBilled)}
      </Td>
      {/* item 611 — the customer's real PO number, captured in the guided approval
          (NOT the proposal number). '—' until a PO is entered. */}
      <Td>{row.poNumber}</Td>
      <Td>
        {editable ? (
          <Input
            size="xs"
            minW="140px"
            type="date"
            value={deploymentDate}
            onChange={(e) => setDeploymentDate(e.target.value)}
            onBlur={() => commit()}
            sx={{ '::-webkit-calendar-picker-indicator': { cursor: 'pointer', opacity: 0.85 } }}
          />
        ) : (
          <Text as="span" color="gray.500">{row.deploymentDate || '—'}</Text>
        )}
      </Td>
      <Td>
        {editable ? (
          <Input
            size="xs"
            minW="180px"
            placeholder="Notes…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => commit()}
          />
        ) : (
          <Text as="span" color="gray.500">{row.notes || '—'}</Text>
        )}
      </Td>
    </Tr>
  );
};

SalesBibleRowEditor.defaultProps = { onLifecycle: undefined, onAssignPm: undefined, onAssignLead: undefined };

export default SalesBibleRowEditor;
